import * as React from 'react';
import { type PageBase, useMatchSmallViewport } from '@avensia/excite';
import { useBundleVariantSelector } from './use-bundle-selectors';
import { useVariantVariesOn } from './use-varies-on-selectors';
import { useProductConfigurationSelectors } from './product-configuration-selectors';
import { useDefaultVariantSelector } from './use-default-variant-selector';

import type { JSX } from 'react';

export type ProductPage = NordicNest.Api.ProductPageApiModel & PageBase;

export type PropType = NordicNest.Api.ProductPageApiModel & {
  currentVariantTicket?: string;
  variants: NordicNest.Api.VariantLinkApiModel[];
  currentVariant: NordicNest.Api.VariantLinkApiModel | undefined;
  onChangeOption?: (selectedVariant?: NordicNest.Api.VariantLinkApiModel) => void;
  optional?: boolean;
  cheapestPrice?: number;
  onRemoveOption?: (option: NordicNest.Api.SelectOptionApiModel) => void;
  placeholder?: string;
  showVariantSelect: boolean;
  variantTransform: (option: OptionType, isSelectedValue: boolean, placeholder?: string) => JSX.Element;
  productConfigurationTransform: (
    selectedOption: NordicNest.Api.SelectOptionApiModel,
    feature: NordicNest.Api.FeatureApiModel,
    isSelectedValue: boolean,
  ) => JSX.Element;
};

export enum SelectType {
  Variant,
  Bundle,
  ProductConfiguration,
  VariesOn,
}

export type Selector = {
  id: string;
  selectedOption: NordicNest.Api.SelectOptionApiModel | undefined;
  value: string | undefined;
  options: NordicNest.Api.SelectOptionApiModel[];
  type: SelectType;
  placeholder?: string;
  optionTransform?: (option: NordicNest.Api.SelectOptionApiModel) => React.ReactNode;
  selectedTransform?: (option: NordicNest.Api.SelectOptionApiModel) => React.ReactNode;
  showVariantSelect?: boolean;
  onChangeOption?: (value: string) => void;
  additionalPrice: Excite.Commerce.AmountWithVatApiModel;
  selectedVariant?: NordicNest.Api.VariantLinkApiModel;
};

type VariantSelectorsContext = {
  open: (selectorId: string) => void;
  close: () => void;
  openSelectId?: string;
  selectors: Selector[];
  isOpen: (selectorId: string) => boolean;
  selectOption: (value: string) => void;
  productConfiguration?: { [item: string]: string };
};

const context = React.createContext<VariantSelectorsContext>({
  selectors: [],
  open: () => {},
  close: () => {},
  isOpen: () => false,
  selectOption: value => {},
});

export function useVariantSelector(selectorId?: string): Selector & VariantSelectorsContext {
  const { selectors, ...rest } = React.useContext(context);
  const id = selectorId ?? DefaultSelectorId;
  const selector = selectors.find(s => s.id === id) as Selector;
  return { ...selector, ...rest, selectors };
}

export function useSelectedVariantSelector(selectorId?: string): Selector {
  const { selectors, openSelectId } = React.useContext(context);
  const id = openSelectId ?? DefaultSelectorId;
  const selector = selectors.find(s => s.id === id) as Selector;

  return { ...selector };
}

export const DefaultSelectorId = 'default';

export type OptionType = NordicNest.Api.SelectOptionApiModel & {
  image?: Excite.ContentApi.ImageApiModel;
  variant?: NordicNest.Api.VariantLinkApiModel;
  showPlusSign?: boolean;
};

export const VariantSelectorsProvider = React.memo((props: React.PropsWithChildren<PropType>) => {
  const [openSelectId, setOpenSelectId] = React.useState<string>('');
  const isSmallViewport = useMatchSmallViewport();
  const selectors = React.useRef<Selector[]>([]);

  const addOrUpdate = React.useCallback((selector: Selector) => {
    const index = selectors.current.findIndex(s => s.id === selector.id);
    if (index >= 0) {
      selectors.current[index] = selector;
    } else {
      selectors.current.push(selector);
    }
  }, []);

  useDefaultVariantSelector(props, isSmallViewport, addOrUpdate, props.variantTransform);
  const productConfiguration = useProductConfigurationSelectors(
    props.productConfiguration,
    addOrUpdate,
    props.productConfigurationTransform,
  );
  useBundleVariantSelector(props, addOrUpdate, props.variantTransform);
  useVariantVariesOn(props, addOrUpdate, isSmallViewport, props.variantTransform);

  const open = React.useCallback((selectorId: string) => {
    setOpenSelectId(selectorId);
  }, []);

  const close = React.useCallback(() => {
    setOpenSelectId('');
  }, []);

  const selectOption = React.useCallback(
    (value: string) => {
      const select = selectors.current.find(s => s.id === openSelectId);
      if (select) {
        select.value = value;
        close();
        select.onChangeOption?.(value);
      }
    },
    [close, openSelectId],
  );

  const isOpen = React.useCallback(
    (selectorId: string) => {
      return openSelectId === selectorId;
    },
    [openSelectId],
  );

  return (
    <context.Provider
      value={{
        close,
        open,
        isOpen,
        selectOption,
        selectors: selectors.current,
        productConfiguration,
      }}
    >
      {props.children}
    </context.Provider>
  );
});
